import {getTimeZones} from '../../../shared/helpers/helpers';
import {LABELS} from '../../../shared/labels/Labels';
import {actions as actionCreators, XeroSignupAction} from '../../actions';
import {DEFAULT_STATE, XeroSignupState} from '../../state';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditions';
import {InfoText, StyledButtonContainer} from '../styled';
import {useGlobalization} from '@planday-corp/globalization';
import Button from '@planday-corp/ui/actions/Button';
import Grid, {GridCell} from '@planday-corp/ui/forms/Grid';
import SelectField from '@planday-corp/ui/forms/fields/SelectField';
import TextField from '@planday-corp/ui/forms/fields/TextField';
import {useMemo} from 'react';
import {connect, ConnectedProps} from 'react-redux';
import {AnyAction, bindActionCreators, Dispatch} from 'redux';
import {styled} from 'styled-components';

const GRID_GAP = '1rem';

const CustomCell = styled.div<{$width: string}>`
  flex: 0 0 calc(${props => props.$width} - ${GRID_GAP});
  width: calc(${props => props.$width} - ${GRID_GAP});
  margin-right: ${GRID_GAP};
`;

export interface SignupFormProps extends XeroSignupState<string> {
  actions: Pick<
    typeof actionCreators,
    | 'init'
    | 'receive'
    | 'updateField'
    | 'save'
    | 'fetchFormData'
    | 'setTenantId'
    | 'proceed'
  >;
}

export function SignupForm({
  actions,
  isLoading,
  displayCountryAndTimeZone,
  formData,
  errors,
  apiErrors,
  validationRules,
  phonePrefixOptions,
  industryOptions,
  employeeOptions,
  countryOptions,
  hasAcceptedTerms,
  hasSaved,
}: PropsFromRedux) {
  const {i18n} = useGlobalization();

  const onFieldChange = (value: string | number | null, name: string) => {
    actions.updateField({[name]: value});
  };

  const timeZoneOptions = useMemo(
    () => getTimeZones(),
    [displayCountryAndTimeZone]
  );

  return (
    <>
      <InfoText element="p" size="body" value={LABELS.theFormIsPrefilled} />
      <Grid size={2}>
        <GridCell>
          <TextField
            isRequired
            label={LABELS.firstName}
            onChange={onFieldChange}
            name="firstName"
            value={formData.firstName}
            error={errors.fields?.firstName || apiErrors.fields?.FirstName}
            isDisabled={isLoading}
          />
        </GridCell>
        <GridCell>
          <TextField
            isRequired
            label={LABELS.lastName}
            onChange={onFieldChange}
            name="lastName"
            value={formData.lastName}
            error={errors.fields?.lastName || apiErrors.fields?.LastName}
            isDisabled={isLoading}
          />
        </GridCell>
      </Grid>
      {validationRules.item.password && (
        <TextField
          isRequired
          label={LABELS.password}
          type={'password'}
          onChange={onFieldChange}
          name="password"
          value={formData.password}
          error={errors.fields?.password || apiErrors.fields?.Password}
          isDisabled={isLoading}
        />
      )}
      {validationRules.item.email && (
        <TextField
          isRequired
          label={LABELS.userNameEmail}
          onChange={onFieldChange}
          name="email"
          value={formData.email}
          error={errors.fields?.email || apiErrors.fields?.Email}
          isDisabled={isLoading}
        />
      )}

      {displayCountryAndTimeZone && (
        <SelectField
          isRequired
          isSearchable
          label={LABELS.country}
          name="countryCode"
          options={countryOptions}
          value={formData.countryCode}
          error={errors.fields?.countryCode || apiErrors.fields?.CountryCode}
          onChange={onFieldChange}
          isLoading={isLoading}
          isDisabled={isLoading}
        />
      )}

      {displayCountryAndTimeZone &&
        timeZoneOptions.length > 0 &&
        formData.countryCode === 'AU' && (
          <SelectField
            label={LABELS.timeZone}
            name="timeZone"
            options={timeZoneOptions}
            value={formData.timeZone}
            onChange={onFieldChange}
            isLoading={isLoading}
            isDisabled={isLoading}
          />
        )}

      <Grid size={2}>
        <CustomCell $width="30%">
          <SelectField
            isRequired
            isSearchable
            label={LABELS.phoneCountryCodePrefix}
            name="phoneCountryCodePrefix"
            options={phonePrefixOptions}
            value={formData.phoneCountryCodePrefix}
            error={
              errors.fields?.phoneCountryCodePrefix ||
              apiErrors.fields?.PhoneCountryCodePrefix
            }
            onChange={onFieldChange}
            isLoading={isLoading}
            isDisabled={isLoading}
          />
        </CustomCell>
        <CustomCell $width="70%">
          <TextField
            isRequired
            label={LABELS.phoneNumber}
            name={'phone'}
            value={formData.phone}
            error={errors.fields?.phone || apiErrors.fields?.PhoneNumber}
            onChange={onFieldChange}
            isDisabled={isLoading}
          />
        </CustomCell>
      </Grid>

      <TextField
        isRequired
        label={LABELS.companyName}
        onChange={onFieldChange}
        name="companyName"
        value={formData.companyName}
        error={errors.fields?.companyName || apiErrors.fields?.CompanyName}
        isDisabled={isLoading}
      />
      <SelectField
        isRequired
        label={LABELS.industry}
        onChange={onFieldChange}
        options={industryOptions}
        name="industry"
        value={formData.industry}
        error={errors.fields?.industry || apiErrors.fields?.Industry}
        isLoading={isLoading}
        isDisabled={isLoading}
      />
      <SelectField
        isRequired
        label={LABELS.employees}
        onChange={onFieldChange}
        options={employeeOptions}
        name="noOfEmployeesRange"
        value={formData.noOfEmployeesRange}
        error={errors.fields?.noOfEmployeesRange || apiErrors.fields?.Employees}
        isLoading={isLoading}
        isDisabled={isLoading}
      />
      {!isLoading && (
        <TermsAndConditions
          isCountryCodeAU={formData.countryCode === 'AU'}
          isLoading={isLoading}
          hasAcceptedTerms={hasAcceptedTerms}
          hasSaved={hasSaved}
          actions={actions}
        />
      )}
      <StyledButtonContainer>
        <Button
          isDisabled={isLoading}
          key="save"
          type="primary"
          onClick={actions.save}
        >
          {i18n.t(LABELS.signup)}
        </Button>
      </StyledButtonContainer>
    </>
  );
}

const mapStateToProps = ({
  xeroSignup,
}: {
  xeroSignup: XeroSignupState<string>;
}) => {
  const {
    isLoading,
    displayCountryAndTimeZone,
    formData,
    errors,
    apiErrors,
    validationRules,
    phonePrefixOptions,
    employeeOptions,
    industryOptions,
    countryOptions,
    hasAcceptedTerms,
    hasSaved,
  } = xeroSignup || DEFAULT_STATE;
  return {
    isLoading,
    displayCountryAndTimeZone,
    formData,
    errors,
    apiErrors,
    validationRules,
    phonePrefixOptions,
    employeeOptions,
    industryOptions,
    countryOptions,
    hasAcceptedTerms,
    hasSaved,
  };
};

export const mapDispatchToProps = (dispatch: Dispatch<XeroSignupAction>) => ({
  actions: bindActionCreators(actionCreators, dispatch as Dispatch<AnyAction>),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(SignupForm);
